/**
 * A container for form controls that applies common design styling and behavior.
 * This component is inspired by the material form field (mat-form-field) and provides a consistent way of styling and handling form fields in the our app.
 * It offers customizable options for:
 * - Choosing the label style, which can be either floating or automatically positioned.
 * - Hiding the required marker.
 */
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  InjectionToken,
  Input,
} from '@angular/core';
import { FormFieldControl } from './form-field-control.directive';
import { FormFieldLabelDirective } from './form-field-label.directive';

/** Type for the available floatLabel values. */
export type FloatLabelType = 'always' | 'auto';

/** Possible appearance styles for the form field. */
export type FormFieldAppearance = 'legacy' | 'fill' | 'outline';

/**
 * Whether the label for form fields should by default `always` or `auto`.
 */
const DEFAULT_FLOAT_LABEL: FloatLabelType = 'auto';

/** Default appearance used by the form field. */
const DEFAULT_APPEARANCE: FormFieldAppearance = 'outline';

/**
 * Injection token that can be used to inject an instances of `FormFieldComponent`. It serves
 * as alternative token to the actual `FormFieldComponent` class which would cause unnecessary
 * retention of the `FormFieldComponent` class and its component metadata.
 */
export const FORM_FIELD_TOKEN = new InjectionToken<FormFieldComponent>(
  'FormFieldComponent',
);

/** Container for form controls that applies common design styling and behavior. */
@Component({
  selector: 'app-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  providers: [{ provide: FORM_FIELD_TOKEN, useExisting: FormFieldComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
})
export class FormFieldComponent implements AfterContentInit {
  /** Whether the required marker should be hidden. */
  @Input()
  get hideRequiredMarker(): boolean {
    return this._hideRequiredMarker;
  }
  set hideRequiredMarker(value: BooleanInput) {
    this._hideRequiredMarker = coerceBooleanProperty(value);
  }
  private _hideRequiredMarker = false;

  /** Whether the label should be float or not. */
  @Input()
  get floatLabel(): FloatLabelType {
    return this._floatLabel || DEFAULT_FLOAT_LABEL;
  }
  set floatLabel(value: FloatLabelType) {
    if (value !== this._floatLabel) {
      this._floatLabel = value;
    }
  }
  private _floatLabel: FloatLabelType;

  /** The form field appearance style. */
  @Input()
  get appearance(): FormFieldAppearance {
    return this._appearance;
  }
  set appearance(value: FormFieldAppearance) {
    this._appearance = value;
  }
  private _appearance: FormFieldAppearance = DEFAULT_APPEARANCE;

  @ContentChild(FormFieldLabelDirective) labelChild:
    | FormFieldLabelDirective
    | undefined;

  @ContentChild(FormFieldControl)
  formFieldControl: FormFieldControl;

  ngAfterContentInit() {
    // this._assertFormFieldControl();
  }

  /** Throws an error if the form field's control is missing. */
  private _assertFormFieldControl() {
    if (!this.formFieldControl) {
      throw Error('app-form-field must contain a FormFieldControl.');
    }
  }
}
